import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../_core/_services/auth.service';
import { DepositConfirmationVm } from '../../_core/_model/payments';
import { NotificationService } from '../../_core/_services/notification.service';
import { ApiService } from '../../_core/_services/api.service';

@Component({
  selector: 'app-deposit-confirmation',
  standalone: false,
  templateUrl: './deposit-confirmation.html',
  styleUrl: './deposit-confirmation.css',
})
export class DepositConfirmation implements OnInit {
  depositId!: string;
  depositData: DepositConfirmationVm | null = null;
  error = '';
  returnUrl: string | null = null;

  constructor(
    private notifyService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.depositId = this.route.snapshot.paramMap.get('depositId') ?? '';
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? null;
    this.depositData = this.route.snapshot.data['deposit'] ?? null;

    if (!this.depositData) {
      this.error = 'Unable to confirm deposit. Please try again.';
      this.notifyService.showError(this.error!, 'Error');
    }

    this.auth.getCurrentUser().subscribe();
  }

  done() {
    this.router.navigateByUrl('/dashboard', { replaceUrl: true });
  }

  backToWallet() {
    if (!this.depositData?.walletId) {
      this.router.navigate(['/account'], {
        queryParams: { tab: 'wallets' },
        replaceUrl: true
      });
      return;
    }

    this.router.navigateByUrl(`/wallets/${this.depositData.walletId}`);
  }

  backToOrder() {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  newDeposit() {
    if (this.depositData?.walletId) {
      this.router.navigateByUrl(`/wallets/deposit/${this.depositData.walletId}`);
      return;
    }

    const currencyCode = this.depositData?.currencyCode?.toUpperCase();

    if (!currencyCode) {
      this.navigateToWallets();
      return;
    }

    this.api.getProfile().subscribe({
      next: (res) => {
        const wallet = (res.data?.wallets || []).find(
          item => item.currency?.code?.toUpperCase() === currencyCode
        );

        if (wallet?.walletId) {
          this.router.navigateByUrl(`/wallets/deposit/${wallet.walletId}`);
          return;
        }

        this.navigateToWallets();
      },
      error: () => {
        this.navigateToWallets();
      }
    });
  }

  private navigateToWallets(): void {
    this.router.navigate(['/account'], {
      queryParams: { tab: 'wallets' },
      replaceUrl: true
    });
  }

  get isSuccess(): boolean {
    const status = (this.depositData?.status || '').toLowerCase();
    return status === 'confirmed' || status === 'completed' || status === 'success';
  }

  get isPending(): boolean {
    return (this.depositData?.status || '').toLowerCase() === 'pending';
  }

  get isFailed(): boolean {
    const status = (this.depositData?.status || '').toLowerCase();
    return status === 'failed' || status === 'cancelled';
  }

  get statusBadgeClass(): string {
    if (this.isSuccess) return 'badge text-bg-success';
    if (this.isFailed) return 'badge text-bg-danger';
    if (this.isPending) return 'badge text-bg-warning';
    return 'badge text-bg-secondary';
  }
}
